/**
 * The phone's Data tab as swipeable cards: the arithmetic, kept pure because the
 * component that uses it is a gesture and cannot be exercised in jsdom. See
 * [mobile.md](../../../../docs/features/mobile.md) — "On a phone the views are
 * cards you swipe".
 */

/** How much of each neighbour shows at the sides, and the gap between cards. */
export const PEEK_PX = 10;
export const GAP_PX = 8;

/** Travel before a gesture is decided as sideways (a card) or up/down (a scroll). */
export const LOCK_PX = 8;

/** One card's width in a viewport `width` wide: the rest is the two peeks and gaps. */
export function cardWidth(width: number): number {
  return Math.max(0, width - 2 * (PEEK_PX + GAP_PX));
}

/**
 * Where card `i` starts, in px from the viewport's left edge, with `index` on
 * screen and the finger `drag` px along.
 */
export function cardLeft(i: number, index: number, drag: number, width: number): number {
  const step = cardWidth(width) + GAP_PX;
  return PEEK_PX + GAP_PX + (i - index) * step + drag;
}

/**
 * The drag as drawn: a third of the finger's travel past either end, so the end
 * gives a little rather than stopping dead — and never wraps (owner, 2026-10-14).
 */
export function resist(drag: number, index: number, count: number): number {
  const pastStart = index === 0 && drag > 0;
  const pastEnd = index >= count - 1 && drag < 0;
  return pastStart || pastEnd ? drag / 3 : drag;
}

/**
 * Which card a released drag lands on: the next one past a quarter of a card,
 * or on a flick, and never past either end.
 *
 * @param velocity px per ms, positive to the right.
 */
export function settle(index: number, drag: number, velocity: number, count: number, width: number): number {
  const far = Math.abs(drag) > cardWidth(width) / 4;
  const flick = Math.abs(velocity) > 0.4 && Math.sign(velocity) === Math.sign(drag);
  if (!far && !flick) return index;
  const next = drag < 0 ? index + 1 : index - 1;
  return Math.min(Math.max(next, 0), count - 1);
}

/** Decide the gesture once it has travelled far enough to say; `null` until then. */
export function axisLock(dx: number, dy: number): "x" | "y" | null {
  if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return null;
  return Math.abs(dx) > Math.abs(dy) ? "x" : "y";
}

/**
 * The title strip's offset for label `i`, as a fraction of one label slot from
 * the middle: 0 is centred, -1 is the left neighbour's place. It follows the
 * drag, so the names slide with the cards.
 */
export function labelOffset(i: number, index: number, drag: number, width: number): number {
  const card = cardWidth(width) + GAP_PX;
  return i - index + (card > 0 ? drag / card : 0);
}

/** Mounted: the card on screen and its two neighbours — the rest wait. */
export function isMounted(i: number, index: number): boolean {
  return Math.abs(i - index) <= 1;
}
